import { ExportButton } from "@/components/ExportButton";
import { useState } from "react";
import {
  CreateButton,
  DatagridConfigurable as Datagrid,
  FunctionField,
  List,
  Pagination,
  ReferenceField,
  SelectColumnsButton,
  ShowButton,
  SimpleList,
  TextField,
  useListContext,
  type Exporter,
  type RaRecord,
} from "react-admin";
import type { SxProps } from "@mui/material";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { formatListDate } from "@/utils/date-format";
import { formatRazaEventStartTimeLabel } from "@/utils/raza-event-time";
import { exportToExcel } from "@/utils/export-to-excel";
import {
  isOhbatRazaEvent,
  razaEventKindLabel,
  razaEventPurposeLabel,
  razaEventTitle,
} from "@/utils/raza-event-display";
import { RazaEventsViewToggle } from "./RazaEventsViewToggle";
import { shouldFlagMissingSadarat, missingSadaratBorderLeft } from "./missing-sadarat-highlight";
import { formatRazaEventSadaratListLabel } from "@/utils/format-raza-event-sadarat-display";
import { razaEventListDateQuery, type RazaEventDateScope } from "./raza-event-date-scope";

const kindFilterChoices = [
  { id: "all", name: "All" },
  { id: "ohbat", name: "Ohbat" },
  { id: "raza", name: "Raza-backed" },
];

function kindPurposeLabel(rec: RaRecord): string {
  if (isOhbatRazaEvent(rec)) return razaEventKindLabel(rec);
  return razaEventPurposeLabel(rec) || "—";
}

const columns = [
  {
    header: "Date",
    field: "date",
    width: 14,
    formatter: (rec: RaRecord, v: unknown) => (v ? formatListDate(String(v)) : ""),
  },
  {
    header: "Kind / purpose",
    field: (rec: RaRecord) => kindPurposeLabel(rec),
    width: 20,
  },
  {
    header: "Time",
    field: (rec: RaRecord) => formatRazaEventStartTimeLabel(rec.startTime as string | undefined),
    width: 12,
  },
  { header: "Host ITS", field: "hostItsNo", width: 12 },
  { header: "Host name", field: "hostName", width: 24 },
  { header: "Host sector", field: "hostSector", width: 22 },
  { header: "Host sub-sector", field: "hostSubSector", width: 22 },
  {
    header: "Sadarat",
    field: (rec: RaRecord) => formatRazaEventSadaratListLabel(rec, { empty: "" }),
    width: 24,
  },
  {
    header: "Raza request",
    field: (rec: RaRecord) => (rec.razaRequestId ? String(rec.razaRequestId) : ""),
    width: 36,
  },
  {
    header: "Khidmatguzar",
    field: (rec: RaRecord) =>
      String(
        (rec.khidmatguzar as { Full_Name?: string } | undefined)?.Full_Name ??
          rec.khidmatguzarItsNo ??
          ""
      ),
    width: 28,
  },
  {
    header: "Zakereen",
    field: (rec: RaRecord) =>
      String(
        (rec.zakereen as { Full_Name?: string } | undefined)?.Full_Name ?? rec.zakereenItsNo ?? ""
      ),
    width: 28,
  },
  { header: "Contact", field: "mobileNo", width: 14 },
];

const exportRazaEvents = (records: RaRecord[]) =>
  exportToExcel(columns, records, {
    filenamePrefix: "raza-events",
    sheetName: "Raza events",
  });

function RazaEventsListToolbar({
  dateScope,
  onDateScopeChange,
  isNarrow,
}: {
  dateScope: RazaEventDateScope;
  onDateScopeChange: (scope: RazaEventDateScope) => void;
  isNarrow: boolean;
}) {
  const { filterValues, setFilters } = useListContext();
  const kind = String(filterValues?.kind ?? "all");

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: { xs: "wrap", md: "nowrap" },
        borderBottom: 1,
        borderColor: "divider",
        px: { xs: 1, sm: 0 },
        pb: 1,
        mb: 1,
        minHeight: 48,
      }}
    >
      <Tabs
        value={dateScope}
        onChange={(_, v: RazaEventDateScope) => onDateScopeChange(v)}
        aria-label="Raza events by date"
        sx={{ minHeight: 40, flexShrink: 0, "& .MuiTab-root": { minHeight: 40, py: 0 } }}
      >
        <Tab label="Upcoming" value="upcoming" />
        <Tab label="Past" value="past" />
      </Tabs>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "nowrap",
          flexShrink: 0,
          ml: "auto",
        }}
      >
        <FormControl size="small" sx={{ width: 140, flex: "0 0 140px" }}>
          <InputLabel id="raza-events-kind-label">Kind</InputLabel>
          <Select
            labelId="raza-events-kind-label"
            label="Kind"
            value={kind}
            onChange={(e) => {
              setFilters({ ...filterValues, kind: e.target.value }, undefined, false);
            }}
          >
            {kindFilterChoices.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                {choice.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!isNarrow && <SelectColumnsButton />}
        <ExportButton />
        <CreateButton />
        <RazaEventsViewToggle hideCreateButton />
      </Box>
    </Box>
  );
}

export default function RazaEventsListView() {
  const theme = useTheme();
  const isNarrow = useMediaQuery((t) => t.breakpoints.down("md"), { noSsr: true });
  const sadaratMissingBorder = missingSadaratBorderLeft(theme);
  const [dateScope, setDateScope] = useState<RazaEventDateScope>("upcoming");
  const listQuery = razaEventListDateQuery(dateScope);
  const dateFilter = {
    ...(listQuery.start ? { start: listQuery.start } : {}),
    ...(listQuery.end ? { end: listQuery.end } : {}),
  };

  const listRowSx = (record: RaRecord): SxProps => ({
    borderBottom: 1,
    borderBottomColor: "divider",
    ...(shouldFlagMissingSadarat(record) ? { borderLeft: sadaratMissingBorder } : {}),
  });

  return (
    <List
      key={dateScope}
      resource="raza-events"
      exporter={exportRazaEvents as unknown as Exporter}
      pagination={<Pagination rowsPerPageOptions={[5, 10, 25, 50]} />}
      filter={dateFilter}
      actions={
        <RazaEventsListToolbar
          dateScope={dateScope}
          onDateScopeChange={setDateScope}
          isNarrow={isNarrow}
        />
      }
      sort={listQuery.sort}
      title={false}
      filterDefaultValues={{ kind: "all" }}
      sx={{
        "& .RaList-actions": {
          width: "100%",
          mb: 0,
          display: "block",
        },
      }}
    >
      {isNarrow ? (
        <SimpleList
          rowClick="show"
          primaryText={(r) => (
            <Box sx={{ minWidth: 0, width: "100%" }}>
              <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>
                {razaEventTitle(r)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {r.date ? formatListDate(String(r.date)) : "—"} · {kindPurposeLabel(r)} ·{" "}
                {formatRazaEventStartTimeLabel(r.startTime as string)}
              </Typography>
            </Box>
          )}
          secondaryText={(r) =>
            [
              (() => {
                const label = formatRazaEventSadaratListLabel(r, { empty: "" });
                return label ? `Sadarat: ${label}` : null;
              })(),
              r.razaRequestId ? `Raza request linked` : null,
              (r.khidmatguzar as { Full_Name?: string } | undefined)?.Full_Name ||
              r.khidmatguzarItsNo
                ? `Khidmat: ${(r.khidmatguzar as { Full_Name?: string })?.Full_Name || String(r.khidmatguzarItsNo)}`
                : null,
              (r.zakereen as { Full_Name?: string } | undefined)?.Full_Name || r.zakereenItsNo
                ? `Zakereen: ${(r.zakereen as { Full_Name?: string })?.Full_Name || String(r.zakereenItsNo)}`
                : null,
              r.mobileNo ? `Contact: ${String(r.mobileNo)}` : null,
              [r.hostSector, r.hostSubSector].filter(Boolean).join(" · ") || null,
            ]
              .filter(Boolean)
              .join(" · ") || "—"
          }
          rowSx={(record, _index) => listRowSx(record)}
        />
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Datagrid
            rowClick={false}
            bulkActionButtons={false}
            sx={{ minWidth: 1480 }}
            rowSx={(record, _index) =>
              shouldFlagMissingSadarat(record) ? { borderLeft: sadaratMissingBorder } : {}
            }
          >
            <FunctionField
              label="Date"
              source="date"
              render={(record: RaRecord) =>
                record?.date ? <span>{formatListDate(String(record.date))}</span> : <span>—</span>
              }
            />
            <FunctionField
              label="Kind / purpose"
              render={(record: RaRecord) => kindPurposeLabel(record)}
            />
            <FunctionField
              label="Time"
              source="startTime"
              render={(record) => formatRazaEventStartTimeLabel(record.startTime as string)}
            />
            <TextField source="hostItsNo" label="Host ITS" />
            <TextField source="hostName" label="Host name" emptyText="—" />
            <TextField source="hostSector" label="Sector" emptyText="—" />
            <TextField source="hostSubSector" label="Sub-sector" emptyText="—" />
            <FunctionField label="Sadarat" render={(r) => formatRazaEventSadaratListLabel(r)} />
            <ReferenceField
              source="razaRequestId"
              reference="razaRequests"
              label="Raza request"
              link="show"
              emptyText="—"
            >
              <TextField source="name" emptyText="View request" />
            </ReferenceField>
            <FunctionField
              label="Khidmatguzar"
              render={(r) => r?.khidmatguzar?.Full_Name || r?.khidmatguzarItsNo || "—"}
            />
            <FunctionField
              label="Zakereen"
              render={(r) => r?.zakereen?.Full_Name || r?.zakereenItsNo || "—"}
            />
            <TextField source="mobileNo" label="Contact" />
            <FunctionField
              label="Show"
              source=""
              render={(record) => <ShowButton resource="raza-events" record={record} />}
            />
          </Datagrid>
        </Box>
      )}
    </List>
  );
}
